/**
 * useEditDateSystemsType hook
 */

// React
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

// Libraries
import {
  Control,
  FieldErrors,
  useForm,
  UseFormHandleSubmit,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import dayjs from 'dayjs';

// Utils
import { FORMAT_DATE, VALIDATION_MESSAGES } from 'utils/constants';

// Router
import { DataSystemApplicationSettingCheck } from 'utils/types';

// Interface
interface UseEditDateSystemsType {
  control: Control<DataSystemApplicationSettingCheck, any>;
  modalConfirm: boolean;
  errors: FieldErrors<DataSystemApplicationSettingCheck>;
  handleSubmit: UseFormHandleSubmit<DataSystemApplicationSettingCheck, undefined>;
  handleUpdate: () => void;
  handleUsageChange: (_value: number) => void;
  watch: UseFormWatch<DataSystemApplicationSettingCheck>;
  trigger: UseFormTrigger<DataSystemApplicationSettingCheck>;
}

// Custom validate
const { START_DATE, END_DATE } = VALIDATION_MESSAGES;

const schema = (minStartDate: Date) =>
  yup.object().shape({
    start_date: yup
      .date()
      .required(START_DATE.REQUIRED)
      .typeError(START_DATE.INVALID)
      .min(
        minStartDate,
        START_DATE.MIN.replace(
          '{minDate}',
          minStartDate ? dayjs(minStartDate).format(FORMAT_DATE)?.toString() : ''
        )
      ),
    end_date: yup
      .date()
      .required(END_DATE.REQUIRED)
      .typeError(END_DATE.INVALID)
      .min(yup.ref('start_date'), END_DATE.MIN_VALUE),
  }) as any;

// Hook
export const useEditDateSystems = ({
  setOpenModelConfirm,
  setIsOpenModalBulkUpdateDate,
  setStartDateBulkUpdate,
  setEndDateBulkUpdate,
  defaultValue,
  listCreatedAtChecked,
}: {
  setOpenModelConfirm: any;
  setIsOpenModalBulkUpdateDate: any;
  setStartDateBulkUpdate: Dispatch<SetStateAction<Date | null>>;
  setEndDateBulkUpdate: Dispatch<SetStateAction<Date | null>>;
  handleClose: () => void;
  defaultValue: DataSystemApplicationSettingCheck | undefined;
  listCreatedAtChecked: string[];
}): UseEditDateSystemsType => {
  const [minStartDate, setMinStartDate] = useState(new Date());
  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    reset,
    clearErrors,
    watch,
    trigger,
  } = useForm<DataSystemApplicationSettingCheck>({
    mode: 'onSubmit',
    resolver: yupResolver<DataSystemApplicationSettingCheck>(schema(minStartDate)),
    reValidateMode: 'onBlur',
    defaultValues: defaultValue,
  });

  useEffect(() => {
    if (defaultValue) {
      reset(defaultValue);
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    setValue('start_date', today);
    setValue('end_date', null);
    clearErrors();
  }, [defaultValue, reset, listCreatedAtChecked]);

  useEffect(() => {
    const uniqueListCreatedAtChecked = Array.from(new Set(listCreatedAtChecked));
    const maxDate = uniqueListCreatedAtChecked.length
      ? new Date(Math.max(...uniqueListCreatedAtChecked.map((date) => new Date(date).getTime())))
      : new Date();
    setMinStartDate(maxDate);
  }, [listCreatedAtChecked]);

  const [modalConfirm] = useState(false);

  const handleUpdate = () => {
    const dataInput = watch();
    setStartDateBulkUpdate(dataInput.start_date);
    setEndDateBulkUpdate(dataInput.end_date);
    setIsOpenModalBulkUpdateDate(false);
    setOpenModelConfirm(true);
  };

  const handleUsageChange = (value: number) => {
    if (value === 0) {
      clearErrors();
    }
    setValue('is_used', value);
    const startDate = watch('start_date');
    if (value && !startDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      setValue('start_date', today);
    }
  };

  return {
    trigger,
    control,
    modalConfirm,
    errors,
    handleSubmit,
    handleUpdate,
    handleUsageChange,
    watch,
  };
};
